import type { Organization } from '@breakoutlearning/firebase-repository/models/Organization'
import type { OrganizationCustomProduct } from '@breakoutlearning/firebase-repository/models/OrganizationCustomProduct'
import type { Product } from '@breakoutlearning/firebase-repository/models/Product'
import type { UserProfileCustomProduct } from '@breakoutlearning/firebase-repository/models/UserProfileCustomProduct'
import { SectionPaymentMethod } from '@breakoutlearning/firebase-repository/types'
import { FormError } from 'components/design-system/form/FormError'
import { useInstructorSectionCubit } from 'hooks/cubits/instructorSection'
import { useImpersonatedBreakoutUser } from 'hooks/profile'
import { PricingSelectionWidget } from 'pages/instructor/classes/PricingSelectionWidget'
import { useCallback, useEffect, useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import type { FormValues } from './schema'

export function ProductSelectorSection({
  form,
  sectionOrganization,
  onRequireProductSelectionChange,
}: {
  form: UseFormReturn<FormValues>
  sectionOrganization: Organization
  onRequireProductSelectionChange: (value: boolean) => void
}) {
  const { control } = form
  const cubit = useInstructorSectionCubit()
  const hideDefaultProducts = sectionOrganization.data.hideDefaultProducts
  const user = useImpersonatedBreakoutUser()
  const defaultSystemProducts = user.defaultSystemProducts
  const section = cubit.section

  const defaultProductsToUse = useMemo(() => {
    if (hideDefaultProducts) return []

    return defaultSystemProducts.isLoaded
      ? defaultSystemProducts.value.models
      : []
  }, [defaultSystemProducts, hideDefaultProducts])

  const customProductsToUse = useMemo(() => {
    return cubit.organizationCustomProducts.isLoaded
      ? cubit.organizationCustomProducts.models
      : []
  }, [cubit.organizationCustomProducts])

  const { sectionPaymentMethod, sectionPassPrice, sectionPassQuantity } =
    section?.data ?? {}

  const existingValidSectionPass = useMemo(
    () =>
      section &&
      sectionPaymentMethod !== undefined &&
      [
        SectionPaymentMethod.sectionPassStripe,
        SectionPaymentMethod.sectionPassCoupon,
        SectionPaymentMethod.sectionPassStripeCoupon,
      ].includes(sectionPaymentMethod) &&
      sectionPassPrice &&
      sectionPassQuantity
        ? { sectionPassPrice, sectionPassQuantity }
        : null,
    [section, sectionPaymentMethod, sectionPassPrice, sectionPassQuantity]
  )

  const productMatches = useCallback(
    (
      product: UserProfileCustomProduct | OrganizationCustomProduct | Product
    ) => {
      return (
        product.data.sectionPassPrice ===
          existingValidSectionPass?.sectionPassPrice &&
        product.data.sectionPassQuantity ===
          existingValidSectionPass?.sectionPassQuantity
      )
    },
    [existingValidSectionPass]
  )
  const matchingDefaultProduct = useMemo(() => {
    if (!existingValidSectionPass) return undefined

    return defaultProductsToUse?.find(productMatches)
  }, [defaultProductsToUse, productMatches, existingValidSectionPass])

  const matchingCustomProduct = useMemo(() => {
    if (!existingValidSectionPass) return undefined
    return customProductsToUse?.find(productMatches)
  }, [customProductsToUse, productMatches, existingValidSectionPass])

  const legacyProductOption = useMemo(
    () =>
      existingValidSectionPass &&
      !matchingCustomProduct &&
      !matchingDefaultProduct
        ? {
            sectionPassPrice: existingValidSectionPass.sectionPassPrice,
            sectionPassQuantity: existingValidSectionPass.sectionPassQuantity,
            isCustom: true as const,
          }
        : undefined,
    [matchingCustomProduct, matchingDefaultProduct, existingValidSectionPass]
  )

  const atLeastOneProduct = useMemo(() => {
    const customProducts = customProductsToUse ?? []
    const defaultProducts = defaultProductsToUse ?? []
    return customProducts.length > 0 || defaultProducts.length > 0
  }, [customProductsToUse, defaultProductsToUse])

  const requireProductSelection = useMemo(() => {
    return atLeastOneProduct
    // this was the original logic, but in LTI context, we always want to require selection
    // && !(section !== undefined && !section.usesSectionPass)
  }, [atLeastOneProduct])

  useEffect(() => {
    onRequireProductSelectionChange(requireProductSelection)
  }, [requireProductSelection, onRequireProductSelectionChange])

  if (!requireProductSelection) return null
  if (!atLeastOneProduct) return null

  return (
    <Controller
      control={control}
      name="selectedProduct"
      render={({ field, fieldState }) => {
        return (
          <div>
            <PricingSelectionWidget
              customProducts={
                customProductsToUse
                  ? customProductsToUse.map((product) => ({
                      productId: product.id,
                      productName: product.data.productName,
                      sectionPassPrice: product.data.sectionPassPrice,
                      sectionPassQuantity: product.data.sectionPassQuantity,
                      isCustom: true,
                    }))
                  : []
              }
              defaultProducts={
                defaultProductsToUse
                  ? defaultProductsToUse.map((product) => ({
                      productId: product.id,
                      productName: product.data.productName ?? undefined,
                      sectionPassPrice: product.data.sectionPassPrice,
                      sectionPassQuantity: product.data.sectionPassQuantity,
                      isCustom: false,
                    }))
                  : []
              }
              legacyProductOption={legacyProductOption}
              onSelectionChange={field.onChange}
              selectedProduct={field.value}
              disabled={false}
              hasCorreOverride={false}
            />
            <FormError error={fieldState.error} />
          </div>
        )
      }}
    />
  )
}
